import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import { parseThemeColor } from '../core/color.utils';
import type { ThemeColorWithShade } from '../theme/theme.model';
import type { LabelStyleResult, LabelVariant } from './label.model';

/**
 * Get label colors based on variant and color.
 *
 * For **primary** and **secondary** variants the shade is fixed by the variant
 * (only the color name matters). For **tertiary**, an explicit shade in the
 * color token (e.g. `'neutral-500'`) overrides the default text shade (700).
 */
export function getLabelStyle(
  themeService: JsxgraphThemeService,
  variant: LabelVariant,
  color: ThemeColorWithShade,
): LabelStyleResult {
  const theme = themeService.getTheme();
  const parsed = parseThemeColor(color);

  switch (variant) {
    case 'primary':
      return {
        backgroundColor: themeService.getColor(parsed.name),
        textColor: theme.labelColors.white,
      };
    case 'secondary':
      return {
        backgroundColor: theme.labelColors.bgLight(parsed.name),
        textColor: theme.labelColors.textDark(parsed.name),
      };
    case 'tertiary': {
      // When an explicit shade is provided (e.g. 'neutral-500'), use it directly.
      // Otherwise fall back to the default tertiary shade (color-700).
      const hasExplicitShade = color !== parsed.name;
      return {
        backgroundColor: 'transparent',
        textColor: hasExplicitShade ? themeService.resolveColorToken(color) : theme.labelColors.textDarker(parsed.name),
      };
    }
  }
}
